/**
 * API functions for list AI session-based processing.
 * These call the /list-ai/* endpoints on leads-server-2.
 */
import { getFreshAccessToken } from '@/lib/supabase'

const LEAD_API_URL_2 = process.env.NEXT_PUBLIC_ASYNC_LEAD_API_URL_2 || "http://0.0.0.0:8087"

export interface ListAIProgress {
    session_id: string
    list_id: number
    session_type: string
    status: string
    total: number
    processed: number
    success_count: number
    match_count: number
    is_complete: boolean
    config: Record<string, any>
    current_lead_name: string
    current_lead_title: string
    current_lead_company: string
    results?: Array<{ linkedin?: string; name?: string; domain?: string; title?: string; matches: boolean; reasoning?: string }>
}

interface StartResponse {
    session_id: string
    status: string
    total: number
}

async function getHeaders(): Promise<Record<string, string>> {
    const token = await getFreshAccessToken()
    return {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    }
}

export async function startListAIColumn(
    sessionId: string,
    requestData: {
        lead_list_id: number
        operation_name: string
        ai_prompt: string
        lead_ids?: number[]
        use_google_search?: boolean
        use_website_scrape?: boolean
        use_linkedin_profile?: boolean
        use_linkedin_posts?: boolean
        use_tech_stack?: boolean
        use_employee_info?: boolean
        use_linkedin_employees?: boolean
        use_job_postings?: boolean
        use_linkedin_company?: boolean
        use_screenshot?: boolean
        use_email_replies?: boolean
        use_engagement?: boolean
        use_team_profiles?: boolean
        use_company_icp?: boolean
        user_linkedin_url?: string
        team_members?: { url: string; context?: string }[]
        custom_system_prompt?: string
        preview_only?: boolean
        preview_lead_ids?: number[]
        include_explanation?: boolean
        use_perplexity?: boolean
        include_daily_events?: boolean
        is_dataset?: boolean
        use_agent?: boolean
        conditions?: { column: string; value: string }[]
        skip_existing?: boolean
        normalize_typographic_dashes?: boolean
    }
): Promise<StartResponse> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/column/start`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            session_id: sessionId,
            ...requestData,
        }),
    })
    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Failed to start AI column: ${error}`)
    }
    return response.json()
}

export async function startListAIFilter(
    sessionId: string,
    requestData: {
        list_id: number
        filter_type: 'leads' | 'companies'
        query: string
        useGoogleSearch?: boolean
        useWebsiteScrape?: boolean
        useLinkedInProfile?: boolean
        useEmployeeInfo?: boolean
        useJobPostings?: boolean
        useEmailReplies?: boolean
        useLinkedInCompany?: boolean
        useLinkedInPosts?: boolean
        useTechStack?: boolean
        useScreenshot?: boolean
        useLinkedInProfileData?: boolean
        useEngagement?: boolean
        useTeamProfiles?: boolean
        useCompanyIcp?: boolean
    }
): Promise<StartResponse> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/filter/start`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            session_id: sessionId,
            ...requestData,
        }),
    })
    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Failed to start AI filter: ${error}`)
    }
    return response.json()
}

export async function getListAIProgress(sessionId: string): Promise<ListAIProgress> {
    const headers = await getHeaders()
    const url = `${LEAD_API_URL_2}/list-ai/progress?session_id=${encodeURIComponent(sessionId)}`
    const response = await fetch(url, {
        method: "GET",
        headers,
    })
    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Failed to get progress (${response.status}): ${error}`)
    }
    return response.json()
}

export async function cancelListAI(sessionId: string): Promise<void> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/cancel?session_id=${encodeURIComponent(sessionId)}`, {
        method: "POST",
        headers,
    })
    if (!response.ok) {
        const error = await response.text()
        console.error(`Failed to cancel AI session ${sessionId}:`, error)
    }
}

export async function cancelAIColumn(leadListId: number, columnName: string): Promise<void> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/ai-column/cancel`, {
        method: "POST",
        headers,
        body: JSON.stringify({ lead_list_id: leadListId, column_name: columnName }),
    })
    if (!response.ok) {
        const error = await response.text()
        console.error(`Failed to cancel AI column '${columnName}':`, error)
    }
}

export interface PreviewSSELead {
    orgLeadId: number
    firstName: string
    lastName: string
    company: string
    company_url?: string
    company_linkedin_id?: string
    linkedin?: string
    email?: string
    title: string
    location?: string
    chosenEmail?: string
    ai_data?: Record<string, any>
    row_data?: Record<string, any>
}

export interface PreviewSSEResult {
    type: "progress" | "result" | "complete"
    lead?: { name: string; title: string; company: string; linkedin: string }
    result?: string
    success?: boolean
    columnName?: string
    org_lead_id?: number
    snippet_tier?: number
    explanation?: string
    current?: number
    total?: number
    leadName?: string
    leadTitle?: string
    leadCompany?: string
    total_processed?: number
    success_count?: number
    operation_name?: string
}

/**
 * Stream AI column preview results via SSE (no DB writes, results returned inline).
 * Returns an AbortController so the caller can cancel the stream.
 */
export async function streamPreviewAIColumn(
    requestData: {
        leads: PreviewSSELead[]
        operation_name: string
        ai_prompt: string
        lead_list_id: number
        use_google_search?: boolean
        use_website_scrape?: boolean
        use_linkedin_profile?: boolean
        use_linkedin_posts?: boolean
        use_tech_stack?: boolean
        use_employee_info?: boolean
        use_linkedin_employees?: boolean
        use_job_postings?: boolean
        use_linkedin_company?: boolean
        use_screenshot?: boolean
        use_email_replies?: boolean
        use_engagement?: boolean
        use_team_profiles?: boolean
        use_company_icp?: boolean
        user_linkedin_url?: string
        team_members?: { url: string; context?: string }[]
        custom_system_prompt?: string
        include_explanation?: boolean
        use_perplexity?: boolean
        include_daily_events?: boolean
        use_agent?: boolean
        is_dataset?: boolean
        normalize_typographic_dashes?: boolean
    },
    onEvent: (event: PreviewSSEResult) => void
): Promise<AbortController> {
    const headers = await getHeaders()
    const controller = new AbortController()

    const response = await fetch(`${LEAD_API_URL_2}/ai-column/process-stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            ...requestData,
            preview_only: true,
        }),
        signal: controller.signal,
    })

    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Failed to start preview stream: ${error}`)
    }

    // Read the SSE stream in the background
    const reader = response.body?.getReader()
    if (reader) {
        const decoder = new TextDecoder()
        ;(async () => {
            let buffer = ""
            try {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    buffer += decoder.decode(value, { stream: true })
                    const lines = buffer.split("\n")
                    buffer = lines.pop() || ""
                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const data = JSON.parse(line.slice(6))
                                onEvent(data)
                            } catch {
                                // skip malformed JSON
                            }
                        }
                    }
                }
            } catch (e: any) {
                if (e.name !== "AbortError") {
                    console.error("[Preview SSE] Stream error:", e)
                }
            }
        })()
    }

    return controller
}

export async function getActiveListAISessions(listId: number): Promise<ListAIProgress[]> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/active?list_id=${listId}`, {
        method: "GET",
        headers,
    })
    if (!response.ok) {
        return []
    }
    const data = await response.json()
    return data.sessions || []
}

// -- My-Accounts AI Filter Session API --

export async function startMyAccountsAIFilter(
    sessionId: string,
    requestData: {
        domain: string
        filter_type: 'leads' | 'companies'
        query: string
        leads?: Array<Record<string, unknown>>
        companies?: Array<Record<string, unknown>>
        useGoogleSearch?: boolean
        useWebsiteScrape?: boolean
        useLinkedInProfile?: boolean
        useEmployeeInfo?: boolean
        useJobPostings?: boolean
        useEmailReplies?: boolean
        useLinkedInCompany?: boolean
        useLinkedInPosts?: boolean
        useTechStack?: boolean
        useScreenshot?: boolean
        useLinkedInProfileData?: boolean
    }
): Promise<StartResponse> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/my-accounts-filter/start`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            session_id: sessionId,
            ...requestData,
        }),
    })
    if (!response.ok) {
        const error = await response.text()
        throw new Error(`Failed to start my-accounts AI filter: ${error}`)
    }
    return response.json()
}

export async function getActiveMyAccountsAISessions(domain: string): Promise<ListAIProgress[]> {
    const headers = await getHeaders()
    const response = await fetch(`${LEAD_API_URL_2}/list-ai/my-accounts-filter/active?domain=${encodeURIComponent(domain)}`, {
        method: "GET",
        headers,
    })
    if (!response.ok) {
        return []
    }
    const data = await response.json()
    return data.sessions || []
}
